"use client";

import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type ComponentProps, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Input, type InputProps } from "./Input";

interface DataListProps {
	inputProps: InputProps;
	dataListProps: ComponentProps<"datalist">;
	options?: string[];
}

export function DataListInput(props: DataListProps) {
	const { options, inputProps, dataListProps } = props;
	const methods = useFormContext();
	const [focus, setFocus] = useState(false);

	const inputValues: string[] | undefined = inputProps.name
		? methods.watch(inputProps.name)
		: undefined;

	const addValue = (value: string) => {
		const newValues = value.split(",").filter((v) => !!v && v !== " " && !inputValues?.includes(v));

		if (inputProps.name && newValues.length) {
			methods.setValue(inputProps.name, inputValues ? [...inputValues, ...newValues] : newValues, {
				shouldValidate: true,
			});
		}
	};

	const removeValue = (value: string) => {
		if (inputProps.name && value && inputValues?.includes(value)) {
			methods.setValue(inputProps.name, [...inputValues.filter((v) => v !== value)], {
				shouldValidate: true,
			});
		}
	};

	return (
		<div className="flex flex-col gap-2">
			<Input
				{...inputProps}
				value={focus ? inputProps.value : ""}
				onChange={(e) => {
					e.preventDefault();
					if (e.target.value.includes(",")) {
						addValue(e.target.value);
						e.currentTarget.value = "";
					}
				}}
				onFocus={() => setFocus(true)}
				onBlur={(e) => {
					addValue(e.target.value);
					e.currentTarget.value = "";
					setFocus(false);
				}}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.preventDefault();
						addValue(e.currentTarget.value);
						e.currentTarget.value = "";
					}
				}}
			/>

			{!!options?.length && (
				<datalist {...dataListProps}>
					{options
						.filter((option) => !inputValues?.includes(option))
						.map((option, index) => (
							<option value={option} key={index} />
						))}
				</datalist>
			)}

			{!!inputValues?.length && (
				<div className="flex flex-wrap gap-2">
					{inputValues.map((value, index) => (
						<button
							key={index}
							type="button"
							className="flex items-center justify-center gap-2 rounded-full border border-shades bg-white px-3 py-1.5 text-primary-medium text-sm leading-none hover:border-shades-medium motion-safe:transition-colors"
							onClick={() => removeValue(value)}
						>
							<FontAwesomeIcon icon={faTimes} size="sm" />
							<span>{value}</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
